import {sanitizeRelativePath} from "./relativePath";
import {SelectedFileInput, toSelectedFile} from "./selection";

/**
 * What a folder capture could NOT carry into the transfer (ui#1308).
 *
 * A transfer is a list of FILES: the shape a folder had survives only as each file's `relativePath`,
 * and the api stores that in a `relative_path VARCHAR(1024)` column whose rules it re-applies
 * server-side (`RelativePathSanitizer`, mirrored by {@link sanitizeRelativePath}). Two things
 * therefore cannot be preserved at all, and both used to disappear without a word:
 *
 * - a subfolder that yields no file has no file to hang its path on (and a bare `album/empty/` path
 *   is rejected by both sanitizers as a directory), so it cannot travel;
 * - a path over the 1024-character cap — or one carrying anything else the sanitizer refuses — is
 *   dropped and the file is registered flat, reshaping the recipient's zip.
 *
 * Preserving either would take an api change, so the honest remedy is to SAY so: this module decides
 * what was lost and turns it into one sentence, and the React layer only shows it. No copy and no
 * React live here (`logic-outside-react`) — the catalog owns the words, in both locales.
 */

/** How many names a loss sentence may list before it collapses the rest into a count. */
export const MAX_NAMED_LOSSES = 3;

/**
 * Which surface produced a capture. It is a rule input rather than trivia: the three see different
 * amounts of the disk, so the same empty result means something different from each (ui#1314).
 *
 * - `drop` traverses the Entries API and NAMES every folder that yielded nothing, so an empty drop
 *   has already reported whatever it lost.
 * - `filePick` offers no folders at all, so an empty result is a cancelled dialog — not a loss.
 * - `folderPick` offers exactly one folder and hands back only a `FileList`, so an empty result is a
 *   folder that brought nothing AND the only trace of it that exists.
 */
export type CaptureSource = "drop" | "filePick" | "folderPick";

/** Everything one capture (a drop, or one picker change) failed to carry. */
export interface SelectionLosses {
	/**
	 * Folder paths that yielded no file anywhere beneath them, in traversal order. Named for what the
	 * capture actually observed: an unreadable folder is indistinguishable from an empty one here, so
	 * the copy this drives must not assert that the folder was empty.
	 */
	foldersWithoutFiles: string[];
	/** Names of files whose captured folder path will not survive sanitization — they arrive flat. */
	flattenedFiles: string[];
	/**
	 * A FOLDER PICK that produced no file anywhere. Separate from {@link foldersWithoutFiles} because
	 * it carries no name and cannot: the picker's whole output is a `FileList`, so an empty one is
	 * everything the page is told. Naming it here rather than inventing a folder name keeps the copy
	 * honest, and keeps the ONE browser case that would otherwise read as a broken button — an engine
	 * that accepts `webkitdirectory` and ignores it (iOS Safari before 18.4) — visible to the sender.
	 */
	pickedFolderWithoutFiles: boolean;
}

/**
 * Which of a capture's files will silently lose their folder path.
 *
 * Deliberately re-runs the SAME {@link sanitizeRelativePath} the registration payload will run rather
 * than re-testing the length here: a second copy of the rule would report a different set the moment
 * either side changed, and the whole point is to name exactly the files that end up flat. A file that
 * never claimed a path (a plain pick or a root-level drop) is not a loss — it was always flat.
 */
function flattenedFileNames(incoming: readonly SelectedFileInput[]): string[] {
	return incoming
		.map(toSelectedFile)
		.filter(({relativePath}) =>
			relativePath !== undefined
			&& relativePath.trim() !== ""
			&& sanitizeRelativePath(relativePath) === undefined)
		.map(({file}) => file.name);
}

/** The full loss report for one capture; `foldersWithoutFiles` comes from the traversal that saw them. */
export function selectionLossesOf(
	incoming: readonly SelectedFileInput[],
	foldersWithoutFiles: readonly string[],
	source: CaptureSource,
): SelectionLosses {
	return {
		foldersWithoutFiles: [...foldersWithoutFiles],
		flattenedFiles: flattenedFileNames(incoming),
		pickedFolderWithoutFiles: source === "folderPick" && incoming.length === 0,
	};
}

/**
 * The sentences a locale supplies for each loss. Both take the names to SHOW plus how many were left
 * out, because a drop can lose hundreds of paths and a toast that grows with the drop is unreadable —
 * and because "and 4 more" pluralizes differently per locale, so the count must reach the catalog.
 */
export interface SelectionLossCopy {
	foldersWithoutFiles: (folders: string[], overflow: number) => string;
	flattenedPaths: (files: string[], overflow: number) => string;
	/** The nameless variant, for a folder PICK: same fact, no folder to name (see the field above). */
	pickedFolderWithoutFiles: string;
}

/** Caps a name list at {@link MAX_NAMED_LOSSES}, reporting how many it hid. */
function bounded(names: readonly string[]): [shown: string[], overflow: number] {
	return [names.slice(0, MAX_NAMED_LOSSES), Math.max(0, names.length - MAX_NAMED_LOSSES)];
}

/**
 * The ONE sentence the sender is shown for a capture, or `null` when nothing was lost.
 *
 * One string rather than a message per loss: the toast host renders a single warning at a time, so
 * two calls would race and the second would erase the first — the same reason the delivery notice is
 * collapsed into one verdict (see {@link ./deliveryOutcome}).
 */
export function selectionLossMessage(losses: SelectionLosses, copy: SelectionLossCopy): string | null {
	const sentences: string[] = [];
	if (losses.pickedFolderWithoutFiles) sentences.push(copy.pickedFolderWithoutFiles);
	if (losses.foldersWithoutFiles.length > 0) {
		sentences.push(copy.foldersWithoutFiles(...bounded(losses.foldersWithoutFiles)));
	}
	if (losses.flattenedFiles.length > 0) {
		sentences.push(copy.flattenedPaths(...bounded(losses.flattenedFiles)));
	}
	return sentences.length === 0 ? null : sentences.join(" ");
}
